import React, { useState, useEffect, useRef } from 'react';
import { Send, Check, User, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitContact } from '../../../api/PublicProfile/contact';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';

interface ContactDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  profileSlug: string;
  source?: string; // "direct", "qr", "share"
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

interface Errors {
  fullName?: string;
  email?: string;
  consent?: string;
}

/** API error structure from Result pattern */
interface ApiError {
  description?: string;
  message?: string;
}

const ContactDrawer: React.FC<ContactDrawerProps> = ({ isOpen, onClose, employeeName, profileSlug, source = 'direct' }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus first input when drawer opens
  useEffect(() => {
    if (isOpen && !isSuccess) {
      setTimeout(() => {
        firstInputRef?.current?.focus();
      }, 100);
    }
  }, [isOpen, isSuccess]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors?.[field as keyof Errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = t('Full name is required');
    }

    if (!formData?.email?.trim()) {
      newErrors.email = t('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = t('Please enter a valid email');
    }

    if (!consentChecked) {
      newErrors.consent = t('You must agree to share your contact information');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitContact(profileSlug, {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || undefined,
        company: formData.company?.trim() || undefined,
        message: formData.message?.trim() || undefined,
        source: source
      });

      if (result?.payload === true) {
        setIsSuccess(true);
        // Note: Contact is persisted via API - no need for client-side logging
      } else if (result?.errors && result.errors.length > 0) {
        // Handle validation or business errors from API
        const apiError = result.errors[0] as ApiError;
        const errorMessage = apiError?.description || apiError?.message || t('Something went wrong. Please try again.');
        setSubmitError(errorMessage);
      } else {
        setSubmitError(t('Something went wrong. Please try again.'));
      }
    } catch (error: unknown) {
      // Error is logged for debugging but contact form shows user-friendly message
      if (error instanceof Error) {
        console.error('Contact submission error:', error.message);
      }
      setSubmitError(t('Unable to submit contact. Please try again later.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) {
      onClose();
      // Reset form after animation
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setConsentChecked(false);
        setIsSuccess(false);
        setErrors({});
        setSubmitError(null);
      }, 300);
    }
  };

  const handleViewProfile = () => {
    handleOpenChange(false);
  };

  const messageLength = formData?.message?.length || 0;
  const maxMessageLength = 300;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        hideCloseButton
        className="fixed top-0 ltr:right-0 rtl:left-0 h-full w-full sm:w-[480px] md:w-[520px] lg:w-[560px] lg:max-w-[50vw] rounded-none translate-x-0 translate-y-0 top-0 left-auto shadow-2xl data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right max-h-full"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <DialogHeader className="flex flex-row items-start justify-between p-6 border-b border-border">
            <div className="flex-1 pe-4">
              <DialogTitle className="text-xl font-semibold text-foreground mb-1">
                {t('Share your contact with')} {employeeName}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                {t('This will send your contact details to the employee. They can choose to reply through their preferred channel.')}
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={isSubmitting}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground"
                aria-label="Close drawer"
              >
                <X className="w-5 h-5" />
              </Button>
            </DialogClose>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div className="relative">
                  <input
                    ref={firstInputRef}
                    type="text"
                    id="fullName"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    className={`w-full px-4 pt-6 pb-2 border rounded-lg text-foreground placeholder-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all ${
                      errors?.fullName ? 'border-destructive' : 'border-input'
                    }`}
                    placeholder="Full Name"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="fullName"
                    className="absolute left-4 top-2 text-xs text-muted-foreground transition-all"
                  >
                    {t('Full Name')} *
                  </label>
                  {errors?.fullName && (
                    <p className="mt-1 text-xs text-destructive">{errors?.fullName}</p>
                  )}
                </div>

                {/* Email */}
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    className={`w-full px-4 pt-6 pb-2 border rounded-lg text-foreground placeholder-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all ${
                      errors?.email ? 'border-destructive' : 'border-input'
                    }`}
                    placeholder="Email Address"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="email"
                    className="absolute left-4 top-2 text-xs text-muted-foreground transition-all"
                  >
                    {t('Email Address')} *
                  </label>
                  {errors?.email && (
                    <p className="mt-1 text-xs text-destructive">{errors?.email}</p>
                  )}
                </div>

                {/* Phone (Optional) */}
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    className="w-full px-4 pt-6 pb-2 border border-input rounded-lg text-foreground placeholder-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all"
                    placeholder="Phone Number"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="phone"
                    className="absolute left-4 top-2 text-xs text-muted-foreground transition-all"
                  >
                    {t('Phone Number')}
                  </label>
                </div>

                {/* Company (Optional) */}
                <div className="relative">
                  <input
                    type="text"
                    id="company"
                    value={formData?.company}
                    onChange={(e) => handleInputChange('company', e?.target?.value)}
                    className="w-full px-4 pt-6 pb-2 border border-input rounded-lg text-foreground placeholder-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all"
                    placeholder="Company / Organization"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="company"
                    className="absolute left-4 top-2 text-xs text-muted-foreground transition-all"
                  >
                    {t('Company / Organization')}
                  </label>
                </div>

                {/* Message (Optional) */}
                <div className="relative">
                  <textarea
                    id="message"
                    value={formData?.message}
                    onChange={(e) => {
                      if (e?.target?.value?.length <= maxMessageLength) {
                        handleInputChange('message', e?.target?.value);
                      }
                    }}
                    rows={4}
                    maxLength={maxMessageLength}
                    className="w-full px-4 pt-6 pb-2 border border-input rounded-lg text-foreground placeholder-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all resize-none"
                    placeholder="Quick context (optional)…"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="message"
                    className="absolute left-4 top-2 text-xs text-muted-foreground transition-all"
                  >
                    {t('Message')}
                  </label>
                  <div className="mt-1 text-right">
                    <span className={`text-xs ${
                      messageLength > maxMessageLength * 0.9 ? 'text-warning' : 'text-muted-foreground'
                    }`}>
                      {messageLength} / {maxMessageLength}
                    </span>
                  </div>
                </div>

                {/* Consent Section */}
                <div className="pt-4 border-t border-border">
                  <label className="flex items-start gap-3 rtl:space-x-reverse cursor-pointer group">
                    <div className="flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={consentChecked}
                        onChange={(e) => {
                          setConsentChecked(e?.target?.checked);
                          if (errors?.consent) {
                            setErrors(prev => ({ ...prev, consent: '' }));
                          }
                        }}
                        disabled={isSubmitting}
                        className="w-5 h-5 text-primary border-input rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm text-foreground group-hover:text-foreground">
                        {t('I understand my contact info will be shared with this employee')}
                      </span>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t('Mowazaf does not display your contact details publicly.')}
                      </p>
                    </div>
                  </label>
                  {errors?.consent && (
                    <p className="mt-2 text-xs text-destructive">{errors?.consent}</p>
                  )}
                </div>

                {/* API Error Message */}
                {submitError && (
                  <div className="flex items-start gap-3 p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{submitError}</p>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={!consentChecked}
                    loading={isSubmitting}
                    fullWidth
                    className="gap-2"
                  >
                    {!isSubmitting && <Send className="w-[18px] h-[18px]" />}
                    <span>{isSubmitting ? t('Sending...') : t('Send contact')}</span>
                  </Button>
                </div>
              </form>
            ) : (
              /* Success State */
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-success" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {t('Contact sent')}
                </h3>
                <p className="text-muted-foreground mb-8 max-w-sm">
                  {t('Your details were shared successfully.')}
                </p>
                <Button
                  variant="outline"
                  onClick={handleViewProfile}
                  className="gap-2"
                >
                  <User className="w-4 h-4" />
                  <span>{t('View profile')}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDrawer;
